import { useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { dialogMotion } from './motion';

export interface LightboxItem {
  src: string;
  alt?: string;
  /** Short name of the photo; the lightbox title. */
  caption?: string;
  /** Category chip beside the counter ("Homes"). */
  chip?: string;
  /** Longer description under the title. */
  desc?: string;
}

/** Gallery caption: the photo title unless it is a generic group name, else a trimmed alt text. */
export function captionOf(g: { title?: string; alt_text?: string }): string {
  if (g.title && !/^(community|apartment)$/i.test(g.title)) return g.title;
  return (g.alt_text || '')
    .split(' with ')[0]
    .replace(/ of a modern apartment complex| at a multifamily property/gi, '')
    .replace(/\.$/, '');
}

export function useLightbox() {
  const [index, setIndex] = useState<number | null>(null);
  return { index, open: (i: number) => setIndex(i), close: () => setIndex(null), setIndex };
}

interface LightboxProps {
  items: LightboxItem[];
  index: number | null;
  onClose: () => void;
  onIndex: (i: number) => void;
  /** Optional call to action under the caption ("View Floor Plans →"). */
  cta?: { label: string; href: string } | null;
}

const roundBtn =
  'inline-flex shrink-0 items-center justify-center rounded-full border border-solid [transition:background-color_var(--q-motion-fast),transform_var(--q-motion-normal)_var(--q-ease-out)] [&_svg]:fill-none [&_svg]:stroke-current [&_svg]:[stroke-linecap:round] [&_svg]:[stroke-linejoin:round] [&_svg]:[stroke-width:1.8]';
const ghostBtn = `${roundBtn} border-[color-mix(in_srgb,var(--q-on-image)_40%,transparent)] text-[--q-on-image] hover:bg-[color-mix(in_srgb,var(--q-on-image)_12%,transparent)]`;
const navBtn = `${ghostBtn} h-14 w-14`;
const mnavBtn = `${navBtn} max-[767px]:h-12 max-[767px]:w-12`;
const figure =
  'm-0 h-full w-full max-w-[930px] overflow-hidden rounded-md animate-[rise_var(--q-motion-slow)_var(--q-ease-out)] max-[767px]:max-w-none max-[767px]:flex-[0_0_100%] max-[767px]:rounded-none';
const img = 'block h-full w-full object-cover';
/** Round close button, shared with the virtual tour frame. */
export const lightboxClose = `${ghostBtn} h-12 w-12 max-[767px]:h-11 max-[767px]:w-11`;

const pad = (n: number) => String(n).padStart(2, '0');
const Arrow = ({ dir }: { dir: 'prev' | 'next' }) => (
  <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
    <path d={dir === 'prev' ? 'M15 5l-7 7 7 7' : 'M9 5l7 7-7 7'} />
  </svg>
);
const Close = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

/** Full-screen photo viewer: counter and category, the photo between large arrows, then its title, description and a CTA. */
export function Lightbox({ items, index, onClose, onIndex, cta }: LightboxProps) {
  const t0 = useRef<{ x: number; y: number } | null>(null);
  // Keeps showing the last photo while the viewer fades out after closing.
  const shown = useRef<number | null>(null);
  const open = index != null && items.length > 0;
  const n = items.length;
  if (open) shown.current = ((index % n) + n) % n;
  if (shown.current == null || !n) return null;
  const i = Math.min(shown.current, n - 1);
  const go = (d: number) => onIndex((i + d + n) % n);
  const item = items[i];
  const next = n > 1 ? items[(i + 1) % n] : null;
  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Content
          aria-describedby={undefined}
          className={`fixed inset-0 z-[80] flex flex-col bg-[--q-ink] text-[--q-on-image] outline-none ${dialogMotion.fade}`}
          onKeyDown={(e) => {
            if (e.key === 'ArrowRight') go(1);
            if (e.key === 'ArrowLeft') go(-1);
          }}
          onTouchStart={(e) => (t0.current = { x: e.touches[0].clientX, y: e.touches[0].clientY })}
          onTouchEnd={(e) => {
            if (!t0.current) return;
            const dx = e.changedTouches[0].clientX - t0.current.x;
            const dy = e.changedTouches[0].clientY - t0.current.y;
            if (dy > 80 && Math.abs(dy) > Math.abs(dx)) onClose();
            else if (Math.abs(dx) > 40 && n > 1) go(dx < 0 ? 1 : -1);
            t0.current = null;
          }}
        >
          <Dialog.Title className="sr-only">{item.caption || 'Photo'}</Dialog.Title>
          <div className="flex h-24 shrink-0 items-center justify-between px-10 max-[767px]:h-[76px] max-[767px]:px-[--gutter] max-[767px]:pt-3">
            <div className="flex items-center gap-5 max-[767px]:gap-3">
              <span className="text-[15px] font-medium leading-5 tracking-[.06em]">{pad(i + 1)} / {pad(n)}</span>
              {item.chip && (
                <span className="inline-flex h-8 items-center rounded-full bg-[color-mix(in_srgb,var(--q-on-image)_14%,transparent)] px-3.5 text-[13px] font-medium leading-4 max-[767px]:h-7 max-[767px]:px-3 max-[767px]:text-[12px]">
                  {item.chip}
                </span>
              )}
            </div>
            <div className="flex items-center gap-5 max-[767px]:gap-3">
              <span className="text-[13px] leading-[18px] opacity-70 max-[767px]:hidden">Esc to close</span>
              <Dialog.Close className={lightboxClose} aria-label="Close">
                <Close />
              </Dialog.Close>
            </div>
          </div>

          <div
            className="flex min-h-0 flex-auto items-center justify-between gap-6 px-10 max-[767px]:flex-[0_0_400px] max-[767px]:p-0"
            onClick={(e) => e.target === e.currentTarget && onClose()}
          >
            {n > 1 && (
              <button className={`${navBtn} max-[767px]:hidden`} type="button" aria-label="Previous photo" onClick={() => go(-1)}>
                <Arrow dir="prev" />
              </button>
            )}
            <div className="flex h-full max-h-[620px] min-w-0 flex-auto justify-center max-[767px]:h-[292px] max-[767px]:justify-start max-[767px]:gap-2 max-[767px]:overflow-hidden">
              <figure className={figure}>
                <VibesitesImage key={item.src} url={item.src} alt={item.alt || item.caption || ''} sizes="(min-width: 768px) 930px, 100vw" className={img} />
              </figure>
              {next && (
                <figure className={`${figure} hidden max-[767px]:block max-[767px]:animate-none max-[767px]:opacity-[.55]`} aria-hidden="true">
                  <VibesitesImage key={next.src} url={next.src} alt="" sizes="100vw" className={img} />
                </figure>
              )}
            </div>
            {n > 1 && (
              <button
                className={`${roundBtn} h-14 w-14 border-[--q-on-image] bg-[--q-on-image] text-[--q-ink] hover:[transform:scale(1.06)] max-[767px]:hidden`}
                type="button"
                aria-label="Next photo"
                onClick={() => go(1)}
              >
                <Arrow dir="next" />
              </button>
            )}
          </div>

          <div className="mx-auto flex w-full max-w-[930px] shrink-0 flex-wrap items-start justify-between gap-6 pb-10 pt-8 max-[767px]:flex-auto max-[767px]:flex-col max-[767px]:flex-nowrap max-[767px]:items-stretch max-[767px]:justify-end max-[767px]:gap-5 max-[767px]:px-[--gutter] max-[767px]:pb-7 max-[767px]:pt-2">
            <div className="flex max-w-[640px] flex-col gap-2.5 max-[767px]:gap-2">
              {item.caption && (
                <div className="font-heading text-[30px] font-light uppercase leading-9 tracking-[.04em] max-[767px]:text-[24px] max-[767px]:leading-[30px]">{item.caption}</div>
              )}
              {item.desc && item.desc !== item.caption && (
                <p className="m-0 text-[15px] leading-6 opacity-[.85] max-[767px]:text-[14px] max-[767px]:leading-[21px]">{item.desc}</p>
              )}
            </div>
            <div className="flex items-center gap-2.5">
              {cta && (
                <a className="btn bg-[--q-on-image] text-[--q-primary-on-paper] max-[767px]:flex-auto max-[767px]:[&.btn]:w-auto" href={cta.href}>
                  {cta.label} <span className="btn__arrow">→</span>
                </a>
              )}
              {n > 1 && (
                <span className="hidden max-[767px]:flex max-[767px]:gap-2.5">
                  <button className={mnavBtn} type="button" aria-label="Previous photo" onClick={() => go(-1)}>
                    <Arrow dir="prev" />
                  </button>
                  <button className={`${mnavBtn} max-[767px]:hover:bg-transparent`} type="button" aria-label="Next photo" onClick={() => go(1)}>
                    <Arrow dir="next" />
                  </button>
                </span>
              )}
            </div>
            <div className="hidden max-[767px]:block max-[767px]:text-center max-[767px]:text-[12px] max-[767px]:leading-4 max-[767px]:opacity-70">Swipe to browse · swipe down to close</div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
